import { ReactNode, useEffect, useState } from "react";

import { Box, Pagination } from "@mui/material";
import dayjs from "dayjs";

import { useTime } from "@/components/Application/TimeContext";

import SearchBar from "@/helpers/components/SearchBar";
import SortMenu from "@/helpers/components/SortMenu/SortMenu";

type Props<T> = {
	items: T[] | undefined;
	itemsPerPage: number;
	children: (items: T[]) => ReactNode;
	getItemName: (item: T) => string;
	getItemDate: (item: T) => Date | undefined;
	initialSortOrder?: "ASC" | "DESC";
};

const PaginatedSearchWrapper = <T,>(props: Props<T>) => {
	const {
		items,
		itemsPerPage = 10,
		initialSortOrder = "ASC",
		children,
		getItemName,
		getItemDate,
	} = props;
	const { timezone } = useTime();
	const [searchParams, setSearchParams] = useState<string>("");
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [filteredItems, setFilteredItems] = useState<any[]>([]);
	const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">(initialSortOrder);

	useEffect(() => {
		const filtered =
			items &&
			items.filter((item) =>
				getItemName(item)?.toLowerCase().includes(searchParams?.toLowerCase()),
			);
		filtered && setFilteredItems(filtered);
	}, [searchParams, items]);

	const handlePageChange = (
		event: React.ChangeEvent<unknown>,
		page: number,
	) => {
		setCurrentPage(page);
	};

	useEffect(() => {
		const sortedItems =
			items &&
			[...items].sort((a, b) => {
				const dateA = dayjs(getItemDate(a)).tz(timezone);
				const dateB = dayjs(getItemDate(b)).tz(timezone);
				if (sortOrder === "ASC") {
					return dateA.isAfter(dateB) ? 1 : -1;
				} else {
					return dateA.isBefore(dateB) ? 1 : -1;
				}
			});
		sortedItems && setFilteredItems(sortedItems);
	}, [sortOrder, items]);

	const paginatedItems = filteredItems.slice(
		(currentPage - 1) * itemsPerPage,
		currentPage * itemsPerPage,
	);
	return (
		<Box>
			<Box display="flex" justifyContent="end" gap={2}>
				<SearchBar
					searchParams={searchParams}
					setSearchParams={setSearchParams}
				/>
				<SortMenu sortOrder={sortOrder} setSortOrder={setSortOrder} />
			</Box>
			{children(paginatedItems)}
			<Pagination
				sx={{ display: "flex", justifyContent: "end" }}
				count={Math.ceil(filteredItems.length / itemsPerPage)}
				page={currentPage}
				onChange={handlePageChange}
			/>
		</Box>
	);
};

export default PaginatedSearchWrapper;
